import _isEmpty from 'lodash/isEmpty';
import _isNil from 'lodash/isNil';
import NotificationsApi from '@Data/notifications/Api';
import { processEndPoints } from '@Services/data/ApiHandler';
import store from '@Services/redux/Store';
import { tryCreateProcessNotificationForTask } from './Helpers';
import {
    IHistoricProcess,
    IProcess,
    IProcessPayloadSchemaInput,
    IProcessPayloadUserInput,
    IRunningProcess,
    ProcessPayloadUserInputType,
} from './Interfaces';
import { ProcessActions } from './Reducer';

const Api = {
    getProcesses: async (callback?: () => void) => {
        const response = await processEndPoints.getProcesses();
        if (response.isSuccess) {
            const processes: Array<IProcess> = response.data;

            store.dispatch(ProcessActions.updateProcesses(processes));

            if (callback) {
                callback();
            }
        }
    },
    setProcess: (process: IProcess, callback?: () => void) => {
        store.dispatch(ProcessActions.setProcess(process));

        if (callback) {
            callback();
        }
    },
    OLDinitialisePayloadData: (
        payloadObj: object,
        projectId: number,
        folderId: string,
        callback?: () => void
    ) => {
        const editableObj = {};
        Object.keys(payloadObj).forEach((key, i) => {
            if (key.toLowerCase().indexOf('project') > -1) {
                editableObj[key] = projectId;
            } else if (key.toLowerCase().indexOf('folder') > -1) {
                editableObj[key] = folderId;
            } else if (key === 'imageUrlFieldName') {
                editableObj[key] = [''];
            } else {
                editableObj[key] = '';
            }
        });
        store.dispatch(ProcessActions.OLDinitialisePayloadData(editableObj));
        if (callback) callback();
    },
    initialisePayloadData: (
        processPayloadInputs: Array<IProcessPayloadSchemaInput>,
        callback?: () => void
    ) => {
        const userInputs: Array<IProcessPayloadUserInput> =
            processPayloadInputs.map((input, i) => {
                let inputType: ProcessPayloadUserInputType =
                    ProcessPayloadUserInputType.FOLDER_FIELDS;

                if (!input.is_folder_field) {
                    if (!_isNil(input.format.enum)) {
                        inputType = ProcessPayloadUserInputType.ENUM_VALUES;
                    } else {
                        inputType =
                            input.format.type === 'string'
                                ? ProcessPayloadUserInputType.TEXT
                                : ProcessPayloadUserInputType.NUMERIC;
                    }
                }

                return {
                    id: i,
                    input: input,
                    type: inputType,
                    isMultiple: input.can_have_multiple,
                    values: [undefined],
                };
            });

        store.dispatch(ProcessActions.initialisePayloadData(userInputs));
        if (callback) {
            callback();
        }
    },
    OLDsetPayloadDataAttribute: (attribute: string, value: any) => {
        store.dispatch(
            ProcessActions.OLDsetPayloadDataAttribute({
                attribute: attribute,
                value: value,
            })
        );
    },
    setPayloadDataAttribute: (
        inputId: number,
        newValue: any,
        valueIndex: number
    ) => {
        store.dispatch(
            ProcessActions.setPayloadDataAttribute({
                inputId: inputId,
                value: newValue,
                valueIndex: valueIndex,
            })
        );
    },
    removePayloadDataAttribute: (inputId: number, valueIndex: number) => {
        store.dispatch(
            ProcessActions.removePayloadDataEntry({
                inputId: inputId,
                valueIndex: valueIndex,
            })
        );
    },
    addPayloadDataAttribute: (inputId: number) => {
        store.dispatch(
            ProcessActions.addPayloadDataEntry({ inputId: inputId })
        );
    },
    runProcess: async (
        selectedProcess: IProcess,
        processSchema: any,
        projectId: number,
        userId: string,
        callback?: (runningProcess: IRunningProcess) => void
    ): Promise<boolean> => {
        const payload = {
            process_payload_schema: processSchema,
            process_type: _isEmpty(selectedProcess.process_type)
                ? 'koru_process'
                : selectedProcess.process_type,
            process_run_fk: selectedProcess.process_id,
            user_fk: userId,
            project_fk: projectId,
            process_identifier_name: selectedProcess.process_identifier_name,
        };

        const response = await processEndPoints.runProcess(payload);

        if (response.isSuccess) {
            const handleTask = (
                taskId: string,
                processId: string,
                processName: string
            ) => {
                const runningProcess: IRunningProcess = {
                    task_id: taskId,
                    process_id: processId,
                    process_name: processName,
                };

                store.dispatch(
                    ProcessActions.addRunningProcess(runningProcess)
                );

                if (callback) {
                    callback(runningProcess);
                }
            };

            // This condition will be changed in the future when we have a better way of handling multiple tasks
            if (Array.isArray(response.data.taskId)) {
                response.data.taskId.forEach(
                    (item: { id: string; name: string }) => {
                        handleTask(item.id, response.data.processId, item.name);
                    }
                );
            } else {
                handleTask(
                    response.data.taskId,
                    response.data.processId,
                    selectedProcess.process_name
                );
            }
            return true;
        } else {
            return false;
        }
    },
    cancelProcess: async (taskId: string, callback?: () => void) => {
        // const response = await processEndPoints.cancelProcess(taskId);
        // if (response.isSuccess) {
        //     if (callback) {
        //         callback();
        //     }
        // }
        if (callback) callback();
    },
    getUserProcessesByProject: async (
        userId: string,
        projectId: number,
        callback?: () => void
    ) => {
        // flush existing process notifications first
        NotificationsApi.removeProcessNotifications(async () => {
            // then get processes for this project
            const response = await processEndPoints.getUserProcessesByProject(
                userId,
                projectId
            );

            if (response.isSuccess) {
                const allProcesses: Array<IHistoricProcess> =
                    response.data.data;

                const pendingProcesses = allProcesses.filter(
                    (p) => p.task_status === 'PENDING'
                );

                pendingProcesses.forEach((p) =>
                    tryCreateProcessNotificationForTask(
                        p.process_name,
                        p.task_id
                    )
                );
            }
        });
    },
};

export default Api;
